// AvailableRoomsBoutique.jsx - Diseño elegante y minimalista para Hotel Boutique
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatMXN, fetchAvailableRooms, processPayment } from '../../services/roomData';
import RoomCardBoutique from './RoomCardBoutique';
import BookingModal from '../Reserva/BookingModal';
import ContactModal from '../ContactModal/ContactModal';
import '../css/AvailableRoomsBoutique.css';
import '../css/RoomCardBoutique.css';

const AvailableRoomsBoutique = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dates, setDates] = useState({ checkIn: '', checkOut: '' });
  const [guests, setGuests] = useState({ adults: 1, children: 0 });
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [contactData, setContactData] = useState({ label: '', number: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showDatePanel, setShowDatePanel] = useState(false);
  const [searchDates, setSearchDates] = useState({ checkIn: '', checkOut: '' });
  
  // Parsear parámetros de URL
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const checkIn = queryParams.get('checkIn') || '';
    const checkOut = queryParams.get('checkOut') || '';
    const adults = parseInt(queryParams.get('adults') || '1', 10);
    const children = parseInt(queryParams.get('children') || '0', 10);
    
    setDates({ checkIn, checkOut });
    setGuests({ adults, children });
  }, [location.search]);
  
  // Cargar habitaciones disponibles
  useEffect(() => {
    if (!dates.checkIn || !dates.checkOut) {
      setLoading(false);
      return;
    }
    
    const loadAvailableRooms = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const data = await fetchAvailableRooms(dates.checkIn, dates.checkOut, 'boutique');
        setAvailableRooms(data.rooms || []);
      } catch (err) {
        console.error('Error loading available rooms:', err);
        setError(err.message || 'Error al cargar habitaciones disponibles');
      } finally {
        setLoading(false);
      }
    };
    
    loadAvailableRooms();
  }, [dates.checkIn, dates.checkOut]);
  
  const handleViewDetails = useCallback((room) => {
    setSelectedRoom(room);
    setIsModalOpen(true);
  }, []);
  
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedRoom(null);
  }, []);

  useEffect(() => {
    setSearchDates({ checkIn: dates.checkIn, checkOut: dates.checkOut });
  }, [dates.checkIn, dates.checkOut]);
  
  const handleOpenDatePanel = useCallback(() => {
    setShowDatePanel(true);
  }, []);

  const handleCloseDatePanel = useCallback(() => {
    setShowDatePanel(false);
  }, []);

  const handleSearchInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setSearchDates((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleUpdateSearch = useCallback(
    (event) => {
      event.preventDefault();
      if (!searchDates.checkIn || !searchDates.checkOut) return;
      if (searchDates.checkIn > searchDates.checkOut) {
        alert('La fecha de salida debe ser posterior a la de llegada');
        return;
      }
      setDates({ checkIn: searchDates.checkIn, checkOut: searchDates.checkOut });
      setShowDatePanel(false);
      navigate(
        `${location.pathname}?checkIn=${searchDates.checkIn}&checkOut=${searchDates.checkOut}&adults=${guests.adults}&children=${guests.children}`,
        { replace: true }
      );
    },
    [searchDates, guests, location.pathname, navigate]
  );
  
  const handleGoToCasaHotel = useCallback(() => {
    navigate(`/habitaciones-disponibles?checkIn=${dates.checkIn}&checkOut=${dates.checkOut}&adults=${guests.adults}&children=${guests.children}`);
  }, [navigate, dates, guests]);

  const handleBook = useCallback(async (bookingData) => {
    try {
      const result = await processPayment(bookingData.paymentInfo);
      
      if (result.success) {
        setTimeout(() => {
          handleCloseModal();
          alert(`🏨 ¡Reserva Confirmada en La Capilla!\n\n📋 ID de Reserva: ${result.bookingId}\n💳 Transacción: ${result.transactionId}\n💰 Total: ${formatMXN(bookingData.total)}\n📧 Confirmación enviada a: ${bookingData.guestInfo.email}\n\n¡Esperamos brindarle una experiencia excepcional!`);
        }, 1000);
      }
    } catch (error) {
      alert(`❌ Transacción Declinada\n\n${error.message}\n\nPor favor, verifique los datos de su tarjeta o utilice otro método de pago.`);
      throw error;
    }
  }, [handleCloseModal]);
  
  // Formato de fecha elegante (parsear como local para evitar desfase UTC)
  const formatDateElegant = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-MX', {
      weekday: 'short',
      day: 'numeric',
      month: 'short'
    });
  };
  
  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('es-MX', {
      day: 'numeric',
      month: 'long'
    });
  };
  
  if (loading) {
    return (
      <div className="available-boutique-page">
        <div className="loading-boutique">
          <div className="spinner-boutique"></div>
          <p className="loading-text">{t('availableRooms.loading')}</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="available-boutique-page">
        <div className="error-boutique">
          <h2 className="error-title">{t('availableRooms.error')}</h2>
          <p className="error-message">{error}</p>
          <button onClick={handleOpenDatePanel} className="btn-boutique-primary">
            {t('availableRooms.backToSearch')}
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="available-boutique-page">
      {/* Header minimalista */}
      <header className="header-boutique">
        <div className="header-container">
          <h1 className="title-boutique">Habitaciones Boutique</h1>
          <div className="dates-boutique">
            <span className="date-item">{formatDateElegant(dates.checkIn)}</span>
            <span className="date-separator">—</span>
            <span className="date-item">{formatDateElegant(dates.checkOut)}</span>
          </div>
          <button onClick={handleOpenDatePanel} className="btn-boutique-outline">
            Modificar búsqueda
          </button>
        </div>
        {showDatePanel && (
          <div className="search-panel-boutique">
            <form className="search-panel-form" onSubmit={handleUpdateSearch}>
              <div className="search-field-boutique">
                <label htmlFor="checkIn">Llegada</label>
                <input
                  type="date"
                  id="checkIn"
                  name="checkIn"
                  value={searchDates.checkIn}
                  onChange={handleSearchInputChange}
                  required
                />
              </div>
              <div className="search-field-boutique">
                <label htmlFor="checkOut">Salida</label>
                <input
                  type="date"
                  id="checkOut"
                  name="checkOut"
                  value={searchDates.checkOut}
                  onChange={handleSearchInputChange}
                  required
                />
              </div>
              <div className="search-actions-boutique">
                <button type="submit" className="btn-boutique-primary">Buscar fechas</button>
                <button type="button" className="btn-boutique-outline" onClick={handleCloseDatePanel}>Cerrar</button>
              </div>
            </form>
          </div>
        )}
      </header>
      <main className="main-boutique">
        {availableRooms.length > 0 ? (
          <div className="rooms-grid-boutique">
            {availableRooms.map((room) => (
              <RoomCardBoutique
                key={room._id || room.id}
                room={room}
                onViewDetails={handleViewDetails}
              />
            ))}
          </div>
        ) : (
          <div className="no-rooms-boutique">
            <p className="no-rooms-text">No hay habitaciones disponibles para esas fechas.</p>
            <button onClick={handleOpenDatePanel} className="btn-boutique-primary">
              Ver otras fechas
            </button>
            <button onClick={handleGoToCasaHotel} className="btn-casa-hotel" style={{marginTop: '15px'}}>
              ¿No te convencen? Revisa en Casa Hotel
            </button>
          </div>
        )}
        {availableRooms.length > 0 && (
          <div className="casa-hotel-option">
            <button onClick={handleGoToCasaHotel} className="btn-casa-hotel">
              ¿No te convencen las habitaciones de boutique? Revisa en Casa Hotel
            </button>
          </div>
        )}
      </main>
      
      {/* Footer minimalista */}
      <footer className="footer-boutique">
        <p>© La Capilla Hotel Boutique 2025</p>
        <div className="footer-links">
          <button onClick={() => { setContactData({ label: 'WhatsApp', number: '+524777347474' }); setContactModalOpen(true); }} className="footer-link">
            Contacto
          </button>
        </div>
      </footer>
      
      <BookingModal
        room={selectedRoom}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onBook={handleBook}
        initialCheckIn={dates.checkIn}
        initialCheckOut={dates.checkOut}
        initialDiscountCode=""
      />
      
      <ContactModal
        isOpen={contactModalOpen}
        label={contactData.label}
        number={contactData.number}
        onCall={() => { window.location.href = `tel:${contactData.number}`; setContactModalOpen(false); }}
        onCopy={() => { navigator.clipboard.writeText(contactData.number); setContactModalOpen(false); }}
        onClose={() => setContactModalOpen(false)}
      />
    </div>
  );
};

export default AvailableRoomsBoutique;